"use client";
import {
  Archive,
  Box,
  Copy,
  Fingerprint,
  Gauge,
  Info,
  ShieldAlert,
} from "lucide-react";
import {
  fingerprintEntry,
} from "../../../lib/intel-pack";
import {
  type DependencyAdvisory,
  type ScanReport,
} from "../../../lib/types";
import {
  formatBytes,
  truncateMiddle,
  severityOrder,
} from "../../shared/format";
import {
  StatusDot,
} from "../../shared/primitives";


export function IntelligenceRail({
  report,
  onCopy,
}: {
  report: ScanReport;
  onCopy: (value: string, message?: string) => void;
}) {
  return (
    <aside className="intel-rail">
      <section>
        <div className="rail-heading">
          <span>SEVERITY</span>
          <Gauge size={14} />
        </div>
        <div className="severity-stack">
          {severityOrder.slice(0, 4).map((severity) => (
            <div key={severity}>
              <span>
                <StatusDot severity={severity} />
                {severity}
              </span>
              <strong>{report.counts[severity]}</strong>
            </div>
          ))}
        </div>
      </section>
      <section>
        <div className="rail-heading">
          <span>FINGERPRINTS</span>
          <Fingerprint size={14} />
        </div>
        <div className="hash-block">
          <label>SHA-512</label>
          <button
            type="button"
            onClick={() => onCopy(report.hashes.sha512, "SHA-512 copied")}
          >
            <code>{truncateMiddle(report.hashes.sha512, 12, 10)}</code>
            <Copy size={12} />
          </button>
          <label>SHA-256</label>
          <button
            type="button"
            onClick={() => onCopy(report.hashes.sha256, "SHA-256 copied")}
          >
            <code>{truncateMiddle(report.hashes.sha256, 12, 10)}</code>
            <Copy size={12} />
          </button>
          <label>SHA-1</label>
          <button
            type="button"
            onClick={() => onCopy(report.hashes.sha1, "SHA-1 copied")}
          >
            <code>{truncateMiddle(report.hashes.sha1, 12, 10)}</code>
            <Copy size={12} />
          </button>
          <label>MD5</label>
          <button
            type="button"
            onClick={() => onCopy(report.hashes.md5, "MD5 copied")}
          >
            <code>{truncateMiddle(report.hashes.md5, 12, 10)}</code>
            <Copy size={12} />
          </button>
          {report.hashes.analyzedSha256 !== report.hashes.sha256 && (
            <>
              <label>INNER SHA-256</label>
              <button
                type="button"
                onClick={() =>
                  onCopy(report.hashes.analyzedSha256, "Inner SHA-256 copied")
                }
              >
                <code>
                  {truncateMiddle(report.hashes.analyzedSha256, 12, 10)}
                </code>
                <Copy size={12} />
              </button>
            </>
          )}
          <label>LAYOUT SHA-256</label>
          <button
            type="button"
            onClick={() =>
              onCopy(
                report.hashes.archiveLayoutSha256,
                "Archive layout fingerprint copied",
              )
            }
          >
            <code>
              {truncateMiddle(report.hashes.archiveLayoutSha256, 12, 10)}
            </code>
            <Copy size={12} />
          </button>
          <label>BEHAVIOR SIMHASH</label>
          <button
            type="button"
            onClick={() =>
              onCopy(
                report.archiveFingerprint.simhash64,
                "Behavior SimHash copied",
              )
            }
          >
            <code>{report.archiveFingerprint.simhash64}</code>
            <Copy size={12} />
          </button>
          <label>PACK ENTRY</label>
          <button
            type="button"
            title="Copy a ready-to-paste jarlab.intel.v2 fingerprint record for this sample"
            onClick={() =>
              onCopy(
                JSON.stringify(
                  fingerprintEntry(report.archiveFingerprint.simhash64),
                  null,
                  2,
                ),
                "Pack fingerprint entry copied",
              )
            }
          >
            <code>{"{ family, simhash64, threshold }"}</code>
            <Copy size={12} />
          </button>
        </div>
      </section>
      <section>
        <div className="rail-heading">
          <span>ARCHIVE</span>
          <Archive size={14} />
        </div>
        <div className="rail-facts">
          <div>
            <span>Entries</span>
            <strong>
              {report.entries
                .filter((entry) => !entry.directory)
                .length.toLocaleString()}
            </strong>
          </div>
          <div>
            <span>Classes</span>
            <strong>{report.classes.length.toLocaleString()}</strong>
          </div>
          <div>
            <span>Entry points</span>
            <strong>{report.entryPoints.length}</strong>
          </div>
          <div>
            <span>Expanded</span>
            <strong>{formatBytes(report.totalUncompressed)}</strong>
          </div>
          <div>
            <span>Signature</span>
            <strong>{report.integrity.status.replaceAll("-", " ")}</strong>
          </div>
          <div>
            <span>Dependencies</span>
            <strong>{report.dependencies.length}</strong>
          </div>
          <div>
            <span>Indicators</span>
            <strong>{report.indicators.length}</strong>
          </div>
          <div>
            <span>Entry hashes</span>
            <strong>{report.entryHashes.length.toLocaleString()}</strong>
          </div>
          <div>
            <span>Nested JARs</span>
            <strong>{report.nestedArchivesInspected}</strong>
          </div>
          <div>
            <span>Decoded strings</span>
            <strong>{report.decodedValueCount.toLocaleString()}</strong>
          </div>
          <div>
            <span>Data paths</span>
            <strong>{report.dataFlows.length.toLocaleString()}</strong>
          </div>
          <div>
            <span>Payloads</span>
            <strong>{report.embeddedPayloads.length.toLocaleString()}</strong>
          </div>
        </div>
      </section>
      {report.dependencies.length > 0 && (
        <section>
          <div className="rail-heading">
            <span>DEPENDENCIES</span>
            <Box size={14} />
          </div>
          <div className="dependency-list">
            {report.dependencies.slice(0, 6).map((dependency) => {
              const coordinate = `${dependency.groupId}:${dependency.artifactId}:${dependency.version}`;
              const advisories = report.dependencyAdvisories.filter(
                (item) => item.coordinate === coordinate,
              );
              const worst = advisories.reduce<DependencyAdvisory | null>(
                (top, item) => {
                  const rank: Record<string, number> = {
                    critical: 3,
                    high: 2,
                    medium: 1,
                    low: 0,
                  };
                  if (!top || rank[item.severity] > rank[top.severity]) return item;
                  return top;
                },
                null,
              );
              return (
                <div key={coordinate}>
                  <span>
                    <strong>{dependency.artifactId}</strong>
                    <small>{dependency.version}</small>
                  </span>
                  {worst ? (
                    <span
                      className="dependency-advisory"
                      title={`${worst.advisoryId} · CVSS ${worst.cvssScore ?? "not published"}`}
                    >
                      <StatusDot severity={worst.severity} />
                      <code>{worst.advisoryId}</code>
                    </span>
                  ) : (
                    <span className="dependency-clear">no known advisory</span>
                  )}
                </div>
              );
            })}
          </div>
          {report.advisoryDataset && (
            <div className="advisory-provenance">
              <p>
                <ShieldAlert size={13} aria-hidden="true" />
                <span>
                  Offline dataset: {report.advisoryDataset.advisoryCount.toLocaleString()}{" "}
                  advisories, cutoff{" "}
                  <strong>{report.advisoryDataset.fetchedAt}</strong>
                </span>
              </p>
              <p className="advisory-provenance-note">
                A dependency absent from this snapshot is not known vulnerable as
                of the cutoff date. That is not the same as not being vulnerable.
              </p>
            </div>
          )}
          {report.advisoryUnchecked.length > 0 && (
            <div className="advisory-unchecked">
              <p>
                {report.advisoryUnchecked.length} coordinate
                {report.advisoryUnchecked.length === 1 ? "" : "s"} could not be
                resolved, so no advisory check ran on them. They are not known to
                be clean.
              </p>
            </div>
          )}
        </section>
      )}
      <section className="static-note">
        <Info size={14} />
        <p>
          Static analysis cannot observe runtime-only behavior. Treat “no obvious
          indicators” as a review result, not a safety guarantee.
        </p>
      </section>
    </aside>
  );
}
